// /home/labdoo/zitech-nexus/frontend/src/pages/admin/ServiceForm.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import './ServiceForm.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Service: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Description: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7V4h16v3" />
      <line x1="9" y1="20" x2="15" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
    </svg>
  ),
  Status: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8V12M12 16H12.01" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sf-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

const ServiceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  const fetchService = useCallback(async () => {
    try {
      const response = await adminAPI.getService(parseInt(id!));
      const service = response.data;
      setFormData({
        name: service.name,
        description: service.description,
        is_active: service.is_active,
      });
    } catch (error) {
      console.error('Error fetching service:', error);
    } finally {
      setFetching(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchService();
    } else {
      setFetching(false);
    }
  }, [id, fetchService]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await adminAPI.updateService(parseInt(id!), formData);
      } else {
        await adminAPI.createService(formData);
      }
      navigate('/dashboard/admin/services');
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="sf-loading-container">
        <div className="sf-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sf-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="sf-loading-text">Loading service details...</p>
      </div>
    );
  }

  return (
    <div className="sf-container">
      {/* Header */}
      <div className="sf-header">
        <div className="sf-header-left">
          <button className="sf-back-btn" onClick={() => navigate('/dashboard/admin/services')}>
            <Icons.Back />
            Back to Services
          </button>
          <h1 className="sf-title">{id ? 'Edit Service' : 'Create New Service'}</h1>
        </div>
        <button className="sf-btn-secondary" onClick={() => navigate('/dashboard/admin/services')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="sf-form">
        <div className="sf-form-grid">
          {/* Left Column */}
          <div className="sf-form-left">
            <div className="sf-card">
              <h3 className="sf-card-title">
                <Icons.Service />
                Service Information
              </h3>
              
              <div className="sf-form-group">
                <label className="sf-form-label">
                  <Icons.Service />
                  Service Name *
                </label>
                <input
                  type="text"
                  required
                  className="sf-form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter service name (e.g., web_development)"
                />
                <span className="sf-form-hint">
                  Use format: web_development, mobile_app_development, software_integration, it_consultancy, training_programs, cloud_services
                </span>
              </div>

              <div className="sf-form-group">
                <label className="sf-form-label">
                  <Icons.Description />
                  Description *
                </label>
                <textarea
                  required
                  rows={6}
                  className="sf-form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service in detail..."
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="sf-form-right">
            <div className="sf-card">
              <h3 className="sf-card-title">
                <Icons.Status />
                Settings
              </h3>
              
              <div className="sf-form-group sf-form-checkbox">
                <label className="sf-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sf-checkbox-input"
                  />
                  <span className="sf-checkbox-text">Active (visible to users)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="sf-form-actions">
          <button 
            type="submit" 
            className="sf-btn-primary" 
            disabled={loading}
          >
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                {id ? 'Update Service' : 'Create Service'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;